import clsx from '../utils/clsx';
import styles from '../styles/Battlefield.module.css';
import { levelStep } from '../engine/battlefield/levels';

/** "4.4 m", and "0 m" without a pointless decimal. */
const metres = (n) => `${Number.isInteger(n) ? n : n.toFixed(1)} m`;

/**
 * What a row says when you hover it.
 *
 * An unreachable row is the useful half of the readout: it says the
 * storey is there and that this combatant cannot get to it from where
 * they stand. An UNAFFORDABLE one is the other half - it says the way
 * up exists and costs more than the action chosen.
 */
function rowTitle(level, { cost, blocked }, allowFree) {
  const free = allowFree ? ' · Alt-click to place without spending' : '';
  if (!level.reachable) return `${level.label} - out of reach from here`;
  if (blocked) return `${level.label} - ${metres(cost)} of movement, more than this action allows${free}`;
  if (cost > 0) return `Climb to ${level.label} - ${metres(cost)} of movement${free}`;
  return `Stand on ${level.label}${free}`;
}

/**
 * BattlefieldLevelPicker - which storey this marker is on.
 *
 * The map has always known that a floating slab is a ceiling from below
 * and a floor from above. Nothing said so, and the only way between the
 * two was a wheel with no marks on it - so whether somebody stood under
 * the gantry or on it came down to how far it had been turned, and
 * getting back up was a matter of luck.
 *
 * VERTICAL, AND HIGHEST AT THE TOP. The thing being chosen is height;
 * a list that ran the other way, or sideways, would need reading rather
 * than glancing at.
 *
 * IT APPEARS ONLY WHERE THERE IS A CHOICE. On a flat column there is one
 * level and it is the ground, and a picker offering it would be a
 * control that does nothing on most of most maps.
 *
 * The arrows are the same act as clicking the neighbour above or below,
 * kept because stepping a storey is the common case and hunting for the
 * right row in a tower is not. A disabled arrow means there is genuinely
 * nowhere that way - `levelStep` skips what cannot be reached, so it
 * never dead-ends on a ceiling a flyer could pass.
 */
const NO_COST = { cost: 0, blocked: false };

/** One storey: its name, its height, and - when climbing costs - its price. */
function LevelRow({ level, cost, allowFree, onPick }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={level.here}
      disabled={!level.reachable || (cost.blocked && !allowFree)}
      className={clsx(styles.levelRow, level.here && styles.levelRowHere)}
      data-testid={`level-${level.top}`}
      /* ALT PLACES WITHOUT PAYING, for whoever is allowed it - the GM
         always, and any player the GM has granted it. Ephemeral on
         purpose: a mode would have to be entered, remembered and left, and
         the one thing worse than a free placement is a free placement
         nobody meant. */
      onClick={(e) => onPick(level, { free: allowFree && e.altKey })}
      title={rowTitle(level, cost, allowFree)}
    >
      <span className={styles.levelName}>{level.label}</span>
      <span className={styles.levelHeight}>{metres(level.top)}</span>
      {cost.cost > 0 && (
        <span className={styles.levelCost} data-cost={cost.cost.toFixed(1)}>
          +{metres(cost.cost)}
        </span>
      )}
    </button>
  );
}

/** A step to the next storey that way, disabled when there is none. */
function LevelArrow({ to, testId, label, glyph, words, nothing, onPick }) {
  return (
    <button
      type="button"
      className={styles.levelArrow}
      data-testid={testId}
      disabled={!to}
      onClick={() => to && onPick(to)}
      title={to ? `${words} ${to.label}` : nothing}
      aria-label={label}
    >
      {glyph}
    </button>
  );
}

export default function BattlefieldLevelPicker({ levels, name, onPick, costs, allowFree = false }) {
  // Highest first, without disturbing the caller's array: `levelStep`
  // and the engine both work lowest-first and must keep doing so.
  const shown = [...levels].reverse();
  return (
    <div className={styles.levelPicker} data-testid="battlefield-levels">
      <LevelArrow
        to={levelStep(levels, 1)}
        testId="level-up"
        label="Up a level"
        glyph="▲"
        words="Up to"
        nothing="Nothing above"
        onPick={onPick}
      />
      <div className={styles.levelList} role="listbox" aria-label={name ? `Level - ${name}` : 'Level'}>
        {shown.map((level) => (
          <LevelRow
            key={level.top}
            level={level}
            cost={costs?.[level.top] ?? NO_COST}
            allowFree={allowFree}
            onPick={onPick}
          />
        ))}
      </div>
      {/* Walking off the edge is still a way down, and the one a fall would
          be rolled for. It is no longer the ONLY way: a one-way list read
          as broken, and for as long as a roof reported impassable it
          genuinely was a trap (v7.97.0). */}
      <LevelArrow
        to={levelStep(levels, -1)}
        testId="level-down"
        label="Down a level"
        glyph="▼"
        words="Down to"
        nothing="Nothing below"
        onPick={onPick}
      />
    </div>
  );
}
